import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api';
import type {
  Conversation,
  ConversationAttachment,
  ConversationListEntry,
} from '../../types';
import { useChat } from '../../store';
import { Avatar } from '../Avatar';
import {
  IconAlert,
  IconArchive,
  IconBell,
  IconBellOff,
  IconChat,
  IconChevronRight,
  IconClock,
  IconClose,
  IconDownload,
  IconExit,
  IconFile,
  IconHeart,
  IconImage,
  IconLock,
  IconPalette,
  IconPencil,
  IconPhone,
  IconSearch,
  IconStar,
  IconTrash,
  IconUsers,
  IconVideo,
} from '../Icon';
import { downscaleAvatar } from '../../utils/image';
import {
  clearThemeOverride,
  setThemeOverride,
  useThemeOverride,
} from '../../utils/chatThemeOverrides';
import { useChatPrefs, type FontSize, type Wallpaper } from '../../utils/chatPrefs';
import { confirmDialog } from '../ConfirmHost';
import type { ActiveCallInfo } from '../CallModal';
import type { InfoSection } from './ConversationInfoPanel';

interface InfoMainProps {
  conversation: ConversationListEntry;
  details: Conversation | null;
  onNavigate: (section: InfoSection) => void;
  onConversationUpdate: (changes: Partial<Conversation>) => void;
  onLeft: () => void;
  onStartCall: (info: ActiveCallInfo) => void;
  onOpenSearch: () => void;
}

const MUTE_OPTIONS: Array<{ label: string; durationHours: number | 'always' | 'off' }> = [
  { label: 'Off', durationHours: 'off' },
  { label: '8 hours', durationHours: 8 },
  { label: '1 week', durationHours: 24 * 7 },
  { label: 'Always', durationHours: 'always' },
];

const DISAPPEARING_OPTIONS: Array<{ label: string; seconds: number | null }> = [
  { label: 'Off', seconds: null },
  { label: '24 hours', seconds: 60 * 60 * 24 },
  { label: '7 days', seconds: 60 * 60 * 24 * 7 },
  { label: '90 days', seconds: 60 * 60 * 24 * 90 },
];

// Per-conversation theme overrides — same enum values used in Settings →
// Chats so the labels stay in sync. The "Use default" option clears any
// override on that field, deferring to the global ChatPrefs.
const WALLPAPER_OPTIONS: Array<{ label: string; value: Wallpaper | null }> = [
  { label: 'Use default', value: null },
  { label: 'Default', value: 'default' },
  { label: 'Indigo', value: 'indigo' },
  { label: 'Forest', value: 'forest' },
  { label: 'Sand', value: 'sand' },
  { label: 'Mono', value: 'mono' },
  { label: 'Aurora', value: 'aurora' },
];
const FONT_SIZE_OPTIONS: Array<{ label: string; value: FontSize | null }> = [
  { label: 'Use default', value: null },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
];

function wallpaperLabel(w: Wallpaper | undefined, globalDefault: Wallpaper): string {
  const effective = w ?? globalDefault;
  const m = WALLPAPER_OPTIONS.find((o) => o.value === effective);
  return w ? (m?.label ?? effective) : `Default (${m?.label ?? effective})`;
}
function fontSizeLabel(f: FontSize | undefined, globalDefault: FontSize): string {
  const effective = f ?? globalDefault;
  const m = FONT_SIZE_OPTIONS.find((o) => o.value === effective);
  return f ? (m?.label ?? effective) : `Default (${m?.label ?? effective})`;
}

function formatDisappearing(s: number | null | undefined): string {
  if (!s) return 'Off';
  const o = DISAPPEARING_OPTIONS.find((d) => d.seconds === s);
  return o?.label ?? `${Math.round(s / 86400)}d`;
}

function isMuted(mutedUntil: string | null | undefined): boolean {
  if (!mutedUntil) return false;
  return new Date(mutedUntil).getTime() > Date.now();
}

export function InfoMain({
  conversation,
  details,
  onNavigate,
  onConversationUpdate,
  onLeft,
  onStartCall,
  onOpenSearch,
}: InfoMainProps) {
  const me = useChat((s) => s.me);
  const onlineUserIds = useChat((s) => s.onlineUserIds);
  const applyConversationUpdate = useChat((s) => s.applyConversationUpdate);
  const removeConversation = useChat((s) => s.removeConversation);
  const setMessages = useChat((s) => s.setMessages);

  const isDm = conversation.type === 'dm';
  // Owners and admins both pass the gate — owner is admin + a "can't
  // demote" tag (mirrors the server's canAdminConversation helper).
  const isAdmin =
    conversation.myRole === 'room_admin' || conversation.myRole === 'room_owner';
  const others = conversation.participants.filter((p) => p.id !== me?.id);

  const [name, setName] = useState(conversation.name ?? '');
  const [topic, setTopic] = useState(conversation.topic ?? '');
  const [savingMeta, setSavingMeta] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [mediaPreview, setMediaPreview] = useState<ConversationAttachment[] | null>(null);
  const [muteOpen, setMuteOpen] = useState(false);
  const [disappearOpen, setDisappearOpen] = useState(false);
  const [wallpaperOpen, setWallpaperOpen] = useState(false);
  const [fontSizeOpen, setFontSizeOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const channels = useChat((s) => s.channels);
  const setConversationChannel = useChat((s) => s.setConversationChannel);
  const themeOverride = useThemeOverride(conversation.id);
  const globalChatPrefs = useChatPrefs();
  const [error, setError] = useState<string | null>(null);
  const [callBusy, setCallBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load a small media preview to render the row thumbnails.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.conversationAttachments(conversation.id, 'media');
        if (!cancelled) setMediaPreview(res.attachments.slice(0, 3));
      } catch {
        if (!cancelled) setMediaPreview([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversation.id]);

  // For DMs, fetch the partner's audience-masked public profile so we can
  // render their About line below the title. For groups, the conversation
  // doesn't have an "about" — group topic plays that role and is already
  // wired below. Re-runs when the conversation's primary "other" changes
  // (which only happens when the user navigates to a different DM).
  const dmPartner = isDm ? others[0] : null;
  const dmPartnerId = dmPartner?.id;
  const [partnerProfile, setPartnerProfile] = useState<{
    about: string | null;
    online: boolean;
  } | null>(null);
  useEffect(() => {
    if (!dmPartnerId) {
      setPartnerProfile(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.userProfile(dmPartnerId);
        if (!cancelled) {
          setPartnerProfile({
            about: res.user.about,
            online: res.user.online,
          });
        }
      } catch {
        if (!cancelled) setPartnerProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dmPartnerId]);

  let title: string;
  let subtitle: string;
  let avatarKey: { id: string; displayName: string | null; email: string };
  let online: boolean | undefined;

  if (isDm) {
    const other = others[0];
    title = other ? other.displayName ?? other.email : 'Empty conversation';
    avatarKey = other
      ? { id: other.id, displayName: other.displayName, email: other.email }
      : { id: conversation.id, displayName: null, email: '?' };
    online = other ? onlineUserIds[other.id] ?? false : undefined;
    subtitle = other ? (online ? 'Online' : other.email) : '';
  } else {
    title = conversation.name ?? others.map((p) => p.displayName ?? p.email).join(', ');
    avatarKey = { id: conversation.id, displayName: title, email: title };
    subtitle = `Group · ${conversation.participants.length} members`;
  }

  const muted = isMuted(details?.mutedUntil);

  async function toggleFavorite() {
    setFavoriteBusy(true);
    try {
      const next = !conversation.isFavorite;
      await api.setFavorite(conversation.id, next);
      const state = useChat.getState();
      const idx = state.conversations.findIndex((c) => c.id === conversation.id);
      if (idx !== -1) {
        const updated = [...state.conversations];
        updated[idx] = { ...updated[idx]!, isFavorite: next };
        useChat.setState({ conversations: updated });
      }
    } catch {
      /* ignore */
    } finally {
      setFavoriteBusy(false);
    }
  }

  async function archive() {
    const ok = await confirmDialog({
      title: 'Archive this conversation?',
      description: 'You can restore it later from Settings → Archived chats.',
      confirmLabel: 'Archive',
    });
    if (!ok) return;
    setArchiveBusy(true);
    try {
      await api.setArchived(conversation.id, true);
      removeConversation(conversation.id);
    } catch {
      /* ignore */
    } finally {
      setArchiveBusy(false);
    }
  }

  async function clearChat() {
    const ok = await confirmDialog({
      title: 'Clear chat history?',
      description:
        "Existing messages stay visible to other members; you just won't see them anymore.",
      confirmLabel: 'Clear',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.clearConversation(conversation.id);
      // Wipe local cache so the message list re-fetches and shows nothing.
      setMessages(conversation.id, [], false);
    } catch {
      setError('Could not clear chat.');
    }
  }

  function exportChat() {
    // Browser handles the download via the export endpoint's Content-Disposition.
    // Cookies travel automatically because it's a same-origin GET.
    window.location.href = api.exportConversationUrl(conversation.id);
  }

  async function leave() {
    const ok = await confirmDialog({
      title: 'Leave this group?',
      description: 'You will stop receiving messages and can be re-added by an admin.',
      confirmLabel: 'Leave group',
      destructive: true,
    });
    if (!ok) return;
    try {
      if (me) await api.removeConversationMember(conversation.id, me.id);
      onLeft();
    } catch {
      setError('Could not leave the group.');
    }
  }

  async function saveMeta() {
    setSavingMeta(true);
    setError(null);
    try {
      const res = await api.updateConversation(conversation.id, {
        name: name.trim() || null,
        topic: topic.trim() || null,
      });
      onConversationUpdate({ name: res.conversation.name, topic: res.conversation.topic });
      applyConversationUpdate(conversation.id, {
        name: res.conversation.name,
        topic: res.conversation.topic,
      });
      setSavedAt(Date.now());
    } catch {
      setError('Could not save changes.');
    } finally {
      setSavingMeta(false);
    }
  }

  async function applyMute(option: (typeof MUTE_OPTIONS)[number]) {
    setMuteOpen(false);
    let mutedUntil: string | null;
    if (option.durationHours === 'off') {
      mutedUntil = null;
    } else if (option.durationHours === 'always') {
      mutedUntil = '9999-12-31T00:00:00.000Z';
    } else {
      mutedUntil = new Date(Date.now() + option.durationHours * 3_600_000).toISOString();
    }
    try {
      const res = await api.updateConversationMember(conversation.id, { mutedUntil });
      onConversationUpdate({ mutedUntil: res.mutedUntil });
    } catch {
      setError('Could not update mute setting.');
    }
  }

  async function startCall(video: boolean) {
    setError(null);
    setCallBusy(true);
    try {
      const res = await api.startCall(conversation.id, video);
      onStartCall({
        roomName: res.roomName,
        conversationId: conversation.id,
        url: res.url,
        token: res.token,
        video,
      });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'calls_not_configured'
          ? 'Calls require LiveKit setup.'
          : 'Could not start call.',
      );
    } finally {
      setCallBusy(false);
    }
  }

  async function uploadGroupAvatar(file: File) {
    setError(null);
    setAvatarBusy(true);
    try {
      const blob = await downscaleAvatar(file);
      const res = await api.uploadConversationAvatar(conversation.id, blob);
      onConversationUpdate({ avatarUpdatedAt: res.avatarUpdatedAt });
      applyConversationUpdate(conversation.id, {
        avatarUpdatedAt: res.avatarUpdatedAt,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        setError('Image is too large.');
      } else if (err instanceof ApiError && err.code === 'not_an_image') {
        setError("That file isn't a recognized image format.");
      } else {
        setError('Could not upload that image.');
      }
    } finally {
      setAvatarBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function removeGroupAvatar() {
    const ok = await confirmDialog({
      title: 'Remove the group photo?',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setError(null);
    setAvatarBusy(true);
    try {
      await api.deleteConversationAvatar(conversation.id);
      onConversationUpdate({ avatarUpdatedAt: null });
      applyConversationUpdate(conversation.id, { avatarUpdatedAt: null });
    } catch {
      setError('Could not remove the photo.');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function applyAdvancedPrivacy(value: boolean) {
    if (!isDm && !isAdmin) {
      setError('Only group admins can change Advanced privacy.');
      return;
    }
    try {
      const res = await api.updateConversation(conversation.id, {
        advancedPrivacy: value,
      });
      onConversationUpdate({ advancedPrivacy: res.conversation.advancedPrivacy });
      applyConversationUpdate(conversation.id, {
        advancedPrivacy: res.conversation.advancedPrivacy,
      });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Only admins can change this setting.'
          : 'Could not update setting.',
      );
    }
  }

  async function applyDisappearing(seconds: number | null) {
    setDisappearOpen(false);
    if (!isDm && !isAdmin) {
      setError('Only group admins can change disappearing-message settings.');
      return;
    }
    try {
      const res = await api.updateConversation(conversation.id, {
        disappearingSeconds: seconds,
      });
      onConversationUpdate({ disappearingSeconds: res.conversation.disappearingSeconds });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Only admins can change this setting.'
          : 'Could not update setting.',
      );
    }
  }

  async function applyChannel(channelId: string | null) {
    setChannelOpen(false);
    // Optimistic local update first; the WS conversation:moved event
    // will arrive shortly and reconcile if anything diverges.
    setConversationChannel(conversation.id, channelId);
    try {
      await api.moveConversationToChannel(conversation.id, channelId);
    } catch (err) {
      // Roll back on failure.
      setConversationChannel(conversation.id, conversation.channelId);
      setError(
        err instanceof ApiError && err.status === 403
          ? "You don't have permission to move this chat."
          : 'Could not move that chat.',
      );
    }
  }

  const muteLabel = muted
    ? details?.mutedUntil && new Date(details.mutedUntil).getFullYear() > 9000
      ? 'Muted'
      : `Muted until ${new Date(details!.mutedUntil!).toLocaleDateString()}`
    : 'Off';

  const currentChannelLabel = (() => {
    if (!conversation.channelId) return 'No channel';
    const c = channels.find((c) => c.id === conversation.channelId);
    return c ? `${c.iconEmoji ?? '#'} ${c.name}` : 'Unknown';
  })();

  return (
    <div className="px-4 py-5 space-y-6">
      {error && (
        <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {/* Header — avatar in a concentric bezel, name, subtitle */}
      <div className="flex flex-col items-center text-center pt-2">
        <div className="relative">
          <div className="p-1.5 rounded-full bg-zinc-900/40 hairline">
            <Avatar
              id={avatarKey.id}
              displayName={avatarKey.displayName}
              email={avatarKey.email}
              online={online}
              size={96}
              kind={isDm ? 'user' : 'conversation'}
              avatarUpdatedAt={isDm ? undefined : conversation.avatarUpdatedAt}
            />
          </div>
          {!isDm && isAdmin && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarBusy}
                title="Change group photo"
                aria-label="Change group photo"
                className="absolute bottom-1 right-1 w-8 h-8 rounded-full glass-chrome hairline-strong text-zinc-100 disabled:opacity-50 transition-all duration-300 ease-spring active:scale-[0.92] flex items-center justify-center hover:bg-zinc-800"
              >
                <IconPencil size={12} strokeWidth={1.75} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/heic,image/gif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void uploadGroupAvatar(f);
                }}
              />
            </>
          )}
        </div>
        <h3 className="text-lg font-semibold tracking-tight mt-4 truncate max-w-full">
          {title}
        </h3>
        <p className="text-xs text-zinc-400 mt-1 truncate max-w-full">{subtitle}</p>
        {isDm && partnerProfile?.about && (
          // Render About below the subtitle — the partner's audience may
          // already have hidden it, in which case partnerProfile.about is
          // null and we just don't show anything (matches WhatsApp's quiet
          // fallback rather than a "[hidden]" indicator that would leak the
          // setting).
          <p className="mt-2 max-w-[80%] whitespace-pre-wrap break-words text-sm text-zinc-300">
            {partnerProfile.about}
          </p>
        )}
        {!isDm && isAdmin && conversation.avatarUpdatedAt && (
          <button
            onClick={removeGroupAvatar}
            disabled={avatarBusy}
            className="mt-2 text-[11px] px-2 py-0.5 rounded-md text-zinc-500 hover:text-rose-300 transition disabled:opacity-50"
          >
            Remove photo
          </button>
        )}
      </div>

      {/* Quick-action row — voice / video / search / favorite */}
      <div className="grid grid-cols-4 gap-2 pt-1">
        <QuickAction
          icon={<IconPhone size={16} strokeWidth={1.5} />}
          label="Voice"
          onClick={() => void startCall(false)}
          disabled={callBusy}
        />
        <QuickAction
          icon={<IconVideo size={16} strokeWidth={1.5} />}
          label="Video"
          onClick={() => void startCall(true)}
          disabled={callBusy}
        />
        <QuickAction
          icon={<IconSearch size={16} strokeWidth={1.5} />}
          label="Search"
          onClick={onOpenSearch}
        />
        <QuickAction
          icon={<IconHeart size={16} strokeWidth={1.5} filled={conversation.isFavorite} />}
          label={conversation.isFavorite ? 'Favorite' : 'Favorite'}
          onClick={toggleFavorite}
          disabled={favoriteBusy}
          highlighted={conversation.isFavorite}
        />
      </div>

      {/* Group meta — name + topic, admin-editable */}
      {!isDm && (
        <div className="space-y-2">
          <div>
            <label className="block text-[11px] text-zinc-500 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isAdmin}
              maxLength={100}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-500 mb-1">Topic</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={!isAdmin}
              maxLength={280}
              placeholder="What's this group about?"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={saveMeta}
                disabled={savingMeta}
                className="rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 px-3 py-1.5 text-xs font-medium text-white transition"
              >
                {savingMeta ? 'Saving…' : 'Save'}
              </button>
              {savedAt && Date.now() - savedAt < 3000 && (
                <span className="text-xs text-emerald-400">Saved.</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Media preview row */}
      <button
        onClick={() => onNavigate('media')}
        className="w-full rounded-2xl hairline hover:hairline-strong overflow-hidden text-left bg-zinc-900/30 surface-deep transition-all duration-300 ease-spring"
      >
        <div className="px-3.5 py-3 flex items-center justify-between">
          <span className="text-sm flex items-center gap-2.5">
            <IconImage size={14} strokeWidth={1.5} className="text-zinc-400" />
            Media, links and docs
          </span>
          <IconChevronRight size={13} className="text-zinc-500" />
        </div>
        {mediaPreview && mediaPreview.length > 0 && (
          <div className="grid grid-cols-3 gap-px bg-zinc-900 border-t border-zinc-900">
            {mediaPreview.map((m) => (
              <div
                key={m.id}
                className="aspect-square bg-zinc-900 overflow-hidden flex items-center justify-center"
              >
                {m.contentType.startsWith('image/') ? (
                  <img
                    src={api.attachmentUrl(m.id)}
                    alt={m.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <IconFile size={24} className="text-zinc-500" />
                )}
              </div>
            ))}
          </div>
        )}
      </button>

      {/* Sections list */}
      <ul className="rounded-2xl hairline overflow-hidden bg-zinc-900/30 surface-deep">
        <RowButton
          icon={<IconStar size={14} />}
          label="Starred messages"
          onClick={() => onNavigate('starred')}
        />
        <RowButton
          icon={<IconLock size={14} />}
          label="Encryption"
          onClick={() => onNavigate('encryption')}
        />
        {!isDm && (
          <RowButton
            icon={<IconUsers size={14} />}
            label={`Members · ${conversation.participants.length}`}
            onClick={() => onNavigate('members')}
          />
        )}
      </ul>

      {/* Per-conversation toggles */}
      <ul className="rounded-2xl hairline overflow-hidden bg-zinc-900/30 surface-deep">
        <RowDropdown
          icon={muted ? <IconBellOff size={14} /> : <IconBell size={14} />}
          label="Mute notifications"
          value={muteLabel}
          open={muteOpen}
          onToggle={() => setMuteOpen((v) => !v)}
          options={MUTE_OPTIONS.map((o) => ({
            label: o.label,
            onClick: () => applyMute(o),
          }))}
        />
        <RowDropdown
          icon={<IconClock size={14} />}
          label="Disappearing messages"
          value={formatDisappearing(details?.disappearingSeconds)}
          open={disappearOpen}
          onToggle={() => setDisappearOpen((v) => !v)}
          options={DISAPPEARING_OPTIONS.map((o) => ({
            label: o.label,
            onClick: () => applyDisappearing(o.seconds),
          }))}
        />
        <RowToggle
          icon={<IconHeart size={14} filled={conversation.isFavorite} />}
          label="Add to favorites"
          checked={conversation.isFavorite}
          onClick={toggleFavorite}
          busy={favoriteBusy}
        />
        {/* Move-to-channel — same affordance as the right-click menu in
            the sidebar; this surfaces it for users already inside the
            conversation. Channel list comes from the user's
            membership, plus a "No channel" option to remove from any
            grouping. */}
        <RowDropdown
          icon={<IconChat size={14} />}
          label="Move to channel"
          value={currentChannelLabel}
          open={channelOpen}
          onToggle={() => setChannelOpen((v) => !v)}
          options={[
            { label: 'No channel', onClick: () => void applyChannel(null) },
            ...channels.map((c) => ({
              label: `${c.iconEmoji ?? '#'} ${c.name}`,
              onClick: () => void applyChannel(c.id),
            })),
          ]}
        />
        <RowToggle
          icon={<IconLock size={14} />}
          label="Advanced privacy"
          checked={conversation.advancedPrivacy}
          onClick={() => void applyAdvancedPrivacy(!conversation.advancedPrivacy)}
        />
      </ul>

      {/* Per-conversation theme override — only affects the current
          user's view (stored in localStorage, never broadcast). When no
          override is set, the global ChatPrefs from Settings → Chats
          take over. */}
      <ul className="rounded-2xl hairline overflow-hidden bg-zinc-900/30 surface-deep">
        <RowDropdown
          icon={<IconPalette size={14} />}
          label="Wallpaper"
          value={wallpaperLabel(themeOverride.wallpaper, globalChatPrefs.wallpaper)}
          open={wallpaperOpen}
          onToggle={() => setWallpaperOpen((v) => !v)}
          options={WALLPAPER_OPTIONS.map((o) => ({
            label: o.label,
            onClick: () => {
              setThemeOverride(conversation.id, { wallpaper: o.value });
              setWallpaperOpen(false);
            },
          }))}
        />
        <RowDropdown
          icon={<IconPalette size={14} />}
          label="Message text size"
          value={fontSizeLabel(themeOverride.fontSize, globalChatPrefs.fontSize)}
          open={fontSizeOpen}
          onToggle={() => setFontSizeOpen((v) => !v)}
          options={FONT_SIZE_OPTIONS.map((o) => ({
            label: o.label,
            onClick: () => {
              setThemeOverride(conversation.id, { fontSize: o.value });
              setFontSizeOpen(false);
            },
          }))}
        />
        {/* Reset is only meaningful when at least one override is set —
            avoids surfacing a button that would be a no-op. */}
        {(themeOverride.wallpaper !== undefined ||
          themeOverride.fontSize !== undefined) && (
          <RowButton
            icon={<IconClose size={14} className="text-zinc-400" />}
            label="Reset to defaults for this chat"
            onClick={() => clearThemeOverride(conversation.id)}
          />
        )}
      </ul>
      <p className="text-[11px] text-zinc-500 px-1 -mt-1 leading-relaxed">
        Theme changes here only affect this chat on this device — other
        members keep their own settings.
      </p>

      {!conversation.advancedPrivacy && (
        /* Export — informational, non-destructive. Hidden when advanced
           privacy is on; the server also refuses the request. */
        <ul className="rounded-2xl hairline overflow-hidden bg-zinc-900/30 surface-deep">
          <RowButton
            icon={<IconDownload size={14} className="text-zinc-400" />}
            label="Export chat"
            onClick={exportChat}
          />
        </ul>
      )}
      {conversation.advancedPrivacy && (
        <p className="text-[11px] text-zinc-500 px-1 leading-relaxed">
          Advanced privacy is on. Export and other mass-extraction surfaces are
          disabled for this conversation.
        </p>
      )}

      {/* Destructive actions */}
      <ul className="rounded-2xl hairline overflow-hidden bg-zinc-900/30 surface-deep">
        <RowButton
          icon={<IconArchive size={14} className="text-zinc-400" />}
          label={archiveBusy ? 'Archiving…' : 'Archive chat'}
          onClick={archive}
          disabled={archiveBusy}
        />
        <RowButton
          icon={<IconAlert size={14} className="text-rose-400" />}
          label={isDm ? 'Report contact' : 'Report group'}
          onClick={() => setReportOpen(true)}
          tone="danger"
        />
        {!isDm && (
          <RowButton
            icon={<IconExit size={14} className="text-rose-400" />}
            label="Exit group"
            onClick={leave}
            tone="danger"
          />
        )}
        <RowButton
          icon={<IconTrash size={14} className="text-rose-400" />}
          label="Clear chat"
          onClick={clearChat}
          tone="danger"
        />
      </ul>

      {reportOpen && (
        <ReportConversationModal
          conversationId={conversation.id}
          isDm={isDm}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}

interface ReportConversationModalProps {
  conversationId: string;
  isDm: boolean;
  onClose: () => void;
}

const REPORT_REASONS: Array<{
  value:
    | 'spam'
    | 'harassment'
    | 'hate_speech'
    | 'sensitive_content'
    | 'impersonation'
    | 'other';
  label: string;
  description: string;
}> = [
  { value: 'spam', label: 'Spam or scam', description: 'Unwanted promotions, links, or fraud attempts.' },
  { value: 'harassment', label: 'Harassment or bullying', description: 'Targeted insults, threats, or unwanted contact.' },
  { value: 'hate_speech', label: 'Hate speech', description: 'Attacks based on identity or protected attributes.' },
  { value: 'sensitive_content', label: 'Sensitive content', description: 'Explicit, violent, or otherwise inappropriate material.' },
  { value: 'impersonation', label: 'Impersonation', description: 'Pretending to be someone else.' },
  { value: 'other', label: 'Other', description: 'Tell us in your own words below.' },
];

function ReportConversationModal({
  conversationId,
  isDm,
  onClose,
}: ReportConversationModalProps) {
  const [reason, setReason] = useState<typeof REPORT_REASONS[number]['value']>('spam');
  const [details, setDetails] = useState('');
  const [includeRecent, setIncludeRecent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    setError(null);
    if (reason === 'other' && !details.trim()) {
      setError('Tell us a bit about what happened.');
      return;
    }
    setBusy(true);
    try {
      await api.reportConversation(conversationId, {
        reason,
        details: details.trim() || undefined,
        includeRecentMessages: includeRecent,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not submit: ${err.code}` : 'Could not submit.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition flex items-center justify-center"
        >
          <IconClose size={13} />
        </button>

        {done ? (
          <div className="py-2 text-center">
            <h2 className="text-base font-semibold tracking-tight">Report submitted</h2>
            <p className="text-xs text-zinc-400 mt-2 max-w-xs mx-auto leading-relaxed">
              Thanks. PGOC IT will review and follow up if more information is
              needed.
            </p>
            <button
              onClick={onClose}
              className="mt-5 rounded-md bg-zinc-900 hover:bg-zinc-800 hairline text-zinc-200 text-xs px-4 py-2 transition"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
              Moderation
            </p>
            <h2 className="text-lg font-semibold tracking-tight">
              {isDm ? 'Report this contact' : 'Report this group'}
            </h2>
            <p className="text-xs text-zinc-400 mt-1.5">
              The reviewer sees your name, the chat, and your reason. Don't use
              this for IT support — that lives in Settings → Help.
            </p>

            <ul className="mt-4 space-y-1.5">
              {REPORT_REASONS.map((r) => (
                <li key={r.value}>
                  <label
                    className={`flex items-start gap-3 px-3 py-2 rounded-md hairline cursor-pointer transition ${
                      reason === r.value ? 'bg-zinc-900' : 'hover:bg-zinc-900/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="mt-1 accent-rose-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{r.label}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{r.description}</p>
                    </div>
                  </label>
                </li>
              ))}
            </ul>

            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={
                reason === 'other'
                  ? 'Required — tell us what happened.'
                  : 'Optional details (helps reviewers triage faster).'
              }
              maxLength={2000}
              rows={3}
              className="w-full mt-4 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
            />

            <label className="mt-3 flex items-start gap-2 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={includeRecent}
                onChange={(e) => setIncludeRecent(e.target.checked)}
                className="mt-0.5 accent-rose-500"
              />
              <span>
                Include a snapshot of the last 50 messages so reviewers can see
                context.{' '}
                <span className="text-zinc-500">
                  Only message ids are stored — bodies stay encrypted in place.
                </span>
              </span>
            </label>

            {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}

            <div className="mt-5 flex items-center gap-2">
              <button
                onClick={submit}
                disabled={busy}
                className="flex-1 rounded-md bg-rose-500 hover:bg-rose-400 disabled:opacity-50 px-3 py-2 text-sm font-medium text-white transition"
              >
                {busy ? 'Submitting…' : 'Submit report'}
              </button>
              <button
                onClick={onClose}
                disabled={busy}
                className="rounded-md bg-zinc-900 hover:bg-zinc-800 hairline disabled:opacity-50 px-3 py-2 text-sm text-zinc-300 transition"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  onClick,
  disabled,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  highlighted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-1.5 py-2.5 rounded-2xl hairline transition-all duration-300 ease-spring active:scale-[0.96] disabled:opacity-50 ${
        highlighted
          ? 'bg-rose-500/10 text-rose-300 hover:bg-rose-500/15'
          : 'bg-zinc-900/40 text-zinc-200 hover:bg-zinc-900/70'
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
    </button>
  );
}

function RowButton({
  icon,
  label,
  onClick,
  disabled,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}) {
  return (
    <li>
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full px-3 py-2.5 flex items-center gap-2.5 transition disabled:opacity-50 ${
          tone === 'danger' ? 'text-rose-400 hover:bg-rose-500/5' : 'hover:bg-zinc-900/50'
        }`}
      >
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 text-left text-sm">{label}</span>
        <IconChevronRight size={14} className="text-zinc-600" />
      </button>
    </li>
  );
}

function RowToggle({
  icon,
  label,
  checked,
  onClick,
  busy,
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <li>
      <button
        onClick={onClick}
        disabled={busy}
        className="w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-zinc-900/50 transition disabled:opacity-50"
      >
        <span className="shrink-0 text-zinc-400">{icon}</span>
        <span className="flex-1 text-left text-sm">{label}</span>
        <span
          className={`shrink-0 relative w-9 h-5 rounded-full transition ${
            checked ? 'bg-emerald-500' : 'bg-zinc-700'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition ${
              checked ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </span>
      </button>
    </li>
  );
}

function RowDropdown({
  icon,
  label,
  value,
  open,
  onToggle,
  options,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  options: Array<{ label: string; onClick: () => void }>;
}) {
  return (
    <li className="relative">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-zinc-900/50 transition"
      >
        <span className="shrink-0 text-zinc-400">{icon}</span>
        <span className="flex-1 text-left text-sm">{label}</span>
        <span className="text-xs text-zinc-400">{value}</span>
        <IconChevronRight
          size={14}
          className={`text-zinc-600 transition ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="bg-zinc-900/40 border-t border-zinc-900">
          {options.map((o) => (
            <button
              key={o.label}
              onClick={o.onClick}
              className={`w-full px-9 py-2 flex items-center text-sm hover:bg-zinc-900 transition text-left ${
                o.label === value ? 'text-indigo-400' : 'text-zinc-300'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
